import React, { useState } from 'react';
import Modal from './Modal';
import { habitsApi } from '../api/client';

interface AddHabitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onHabitAdded: () => void;
}

const AddHabitModal: React.FC<AddHabitModalProps> = ({ isOpen, onClose, onHabitAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'health' as 'health' | 'productivity' | 'personal' | 'social' | 'learning',
    frequency: 'daily' as 'daily' | 'weekly' | 'monthly' | 'custom',
    targetValue: '',
    unit: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'health',
      frequency: 'daily',
      targetValue: '',
      unit: '',
      isActive: true
    });
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Habit name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await habitsApi.createHabitDefinition({
        name: formData.name,
        description: formData.description || undefined,
        category: formData.category,
        frequency: formData.frequency,
        targetValue: formData.targetValue ? parseFloat(formData.targetValue) : undefined,
        unit: formData.unit || undefined,
        isActive: formData.isActive
      });

      setSuccess(true);
      setTimeout(() => {
        onHabitAdded();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error adding habit:', err);
      setError('Failed to add habit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New Habit">
      <form onSubmit={handleSubmit} className="modal-form">
        {error && (
          <div className="modal-error">
            {error}
          </div>
        )}

        {success && (
          <div className="modal-success">
            Habit created successfully!
          </div>
        )}

        <div className="modal-form-group">
          <label>Habit Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Drink 8 glasses of water, Read for 30 minutes"
            disabled={loading}
          />
        </div>

        <div className="modal-form-group">
          <label>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Optional description of your habit..."
            disabled={loading}
            rows={2}
          />
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
              disabled={loading}
            >
              <option value="health">Health</option>
              <option value="productivity">Productivity</option>
              <option value="personal">Personal</option>
              <option value="social">Social</option>
              <option value="learning">Learning</option>
            </select>
          </div>

          <div className="modal-form-group">
            <label>Frequency</label>
            <select
              value={formData.frequency}
              onChange={(e) => setFormData({ ...formData, frequency: e.target.value as any })}
              disabled={loading}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>Target Value</label>
            <input
              type="number"
              step="0.1"
              value={formData.targetValue}
              onChange={(e) => setFormData({ ...formData, targetValue: e.target.value })}
              placeholder="e.g., 8, 30, 10000"
              disabled={loading}
            />
          </div>

          <div className="modal-form-group">
            <label>Unit</label>
            <input
              type="text"
              value={formData.unit}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              placeholder="e.g., glasses, minutes, steps"
              disabled={loading}
            />
          </div>
        </div>

        <div className="modal-form-group">
          <label>
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              disabled={loading}
            />
            <span style={{ marginLeft: '8px' }}>Active (start tracking immediately)</span>
          </label>
        </div>

        <div className="modal-form-actions">
          <button
            type="button"
            onClick={handleClose}
            className="modal-btn modal-btn-cancel"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="modal-btn modal-btn-primary"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Habit'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddHabitModal;